import React, { useEffect, useState } from "react";
import { Search, X, Filter } from "lucide-react";
import { ConexionAPI, TIPO_META } from "../../lib/conexion";
import { UbicacionesAPI } from "../../lib/ubicaciones";
import PublicacionCard from "./PublicacionCard";

export default function ConexionBuscar() {
  const [q, setQ] = useState("");
  const [tipo, setTipo] = useState("");
  const [categoria, setCategoria] = useState("");
  const [region, setRegion] = useState("");
  const [provincia, setProvincia] = useState("");
  const [distrito, setDistrito] = useState("");
  const [regiones, setRegiones] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [distritos, setDistritos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    ConexionAPI.catalogos().then((c) => {
      setCategorias(c.categorias);
      setTipos(c.tipos);
    });
    UbicacionesAPI.regiones().then(setRegiones);
  }, []);

  useEffect(() => {
    if (region) UbicacionesAPI.provincias(region).then(setProvincias);
    else setProvincias([]);
    setProvincia("");
    setDistrito("");
  }, [region]);

  useEffect(() => {
    if (region && provincia) UbicacionesAPI.distritos(region, provincia).then(setDistritos);
    else setDistritos([]);
    setDistrito("");
  }, [region, provincia]);

  // Búsqueda en tiempo real: se re-ejecuta al cambiar cualquier filtro o el texto
  // (con un pequeño debounce para no golpear el backend en cada tecla).
  useEffect(() => {
    const t = setTimeout(() => {
      ejecutar();
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, tipo, categoria, region, provincia, distrito]);

  async function ejecutar(e) {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      const filtros = {};
      if (q.trim()) filtros.q = q.trim();
      if (tipo) filtros.tipo = tipo;
      if (categoria) filtros.categoria = categoria;
      if (region) filtros.region = region;
      if (provincia) filtros.provincia = provincia;
      if (distrito) filtros.distrito = distrito;
      filtros.limite = 50;
      const r = await ConexionAPI.buscar(filtros);
      setResultados(r);
    } finally {
      setLoading(false);
    }
  }

  function limpiar() {
    setQ("");
    setTipo("");
    setCategoria("");
    setRegion("");
    setProvincia("");
    setDistrito("");
    // El efecto de búsqueda en tiempo real recargará la lista completa.
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6" data-testid="conexion-buscar">
      <aside className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5 h-fit lg:sticky lg:top-24">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-slate-900 inline-flex items-center gap-2">
            <Filter size={16} /> Filtros
          </h3>
          <button onClick={limpiar} className="text-xs text-brand-red font-semibold hover:underline" data-testid="conexion-buscar-limpiar">
            Limpiar
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Tipo</label>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              data-testid="conexion-buscar-tipo"
              className="mt-1 w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none"
            >
              <option value="">Todos</option>
              {tipos.map((t) => (
                <option key={t} value={t}>{TIPO_META[t]?.label || t}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Categoría</label>
            <select
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              data-testid="conexion-buscar-categoria"
              className="mt-1 w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none"
            >
              <option value="">Todas</option>
              {categorias.map((c) => (
                <option key={c.id} value={c.nombre}>{c.nombre}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Región</label>
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              data-testid="conexion-buscar-region"
              className="mt-1 w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none"
            >
              <option value="">Todas ({regiones.length})</option>
              {regiones.map((r) => (
                <option key={r.codigo} value={r.nombre}>{r.nombre}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Provincia</label>
            <select
              disabled={!region}
              value={provincia}
              onChange={(e) => setProvincia(e.target.value)}
              data-testid="conexion-buscar-provincia"
              className="mt-1 w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none disabled:bg-slate-50 disabled:text-slate-400"
            >
              <option value="">{region ? "Todas" : "Selecciona región"}</option>
              {provincias.map((p) => (
                <option key={p.codigo} value={p.nombre}>{p.nombre}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Distrito</label>
            <select
              disabled={!provincia}
              value={distrito}
              onChange={(e) => setDistrito(e.target.value)}
              data-testid="conexion-buscar-distrito"
              className="mt-1 w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none disabled:bg-slate-50 disabled:text-slate-400"
            >
              <option value="">{provincia ? "Todos" : "Selecciona provincia"}</option>
              {distritos.map((d) => (
                <option key={d.codigo} value={d.nombre}>{d.nombre}</option>
              ))}
            </select>
          </div>

          <button
            onClick={() => ejecutar()}
            data-testid="conexion-buscar-aplicar"
            className="w-full inline-flex items-center justify-center gap-2 bg-brand-blue hover:bg-blue-900 text-white font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors"
          >
            Aplicar filtros
          </button>
        </div>
      </aside>

      <div className="space-y-5">
        <form onSubmit={ejecutar} className="flex items-center bg-white rounded-2xl border border-slate-200 shadow-soft overflow-hidden focus-within:ring-2 focus-within:ring-brand-red/40">
          <div className="pl-4 text-slate-400"><Search size={18} /></div>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="conexion-buscar-input"
            placeholder="Busca por título o descripción…"
            className="flex-1 px-3 py-3.5 text-base text-slate-800 bg-transparent outline-none placeholder:text-slate-400"
          />
          {q && (
            <button type="button" onClick={() => setQ("")} className="text-slate-400 hover:text-slate-600 px-2" aria-label="Limpiar">
              <X size={18} />
            </button>
          )}
          <button
            type="submit"
            data-testid="conexion-buscar-submit"
            className="bg-brand-red hover:bg-brand-red-hover text-white py-3.5 px-6 font-semibold text-sm"
          >
            Buscar
          </button>
        </form>

        {loading ? (
          <div className="text-center py-16 text-slate-500 text-sm">Buscando…</div>
        ) : resultados.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
            <p className="text-slate-600 font-semibold">Sin resultados</p>
            <p className="text-slate-500 text-sm mt-1">Intenta con otros filtros o palabras clave.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500">
              <span className="font-semibold text-slate-700">{resultados.length}</span> resultado{resultados.length !== 1 ? "s" : ""}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="conexion-buscar-resultados">
              {resultados.map((p) => (
                <PublicacionCard key={p.id} pub={p} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
